import type {
  InternalEligibilityPerson,
  InternalEligibilityResponse,
  LegacyEligibilityStatus,
  LegacyPatientInfo,
} from '../../hooks/use-eligibility-verifier';
import {
  buildRows,
  DiffRow,
  FieldSpec,
  present,
  ProviderComparator,
  ProviderModel,
} from './types';

// E-file model.
//
// The field set is what `WaystarWriter` actually reads, since a field nothing
// reads isn't worth a verdict:
//
//   * `WaystarWriterMemberInfoBase`, built from the subscriber and, when there
//     is one, the dependent
//   * the two values `Insurance.apply_row_coverage` writes onto the claim
//     (relationship, subscriber_identifier)
//   * client_id / insurer_id, which gate a billing run through its client- and
//     insurer-change detection
//
// Base `WaystarWriter` passes no `extras_required`, so it reads no
// `extra_columns` at all — that is the subclasses' business, and each arrives
// with its own model. This is the demographic block every Waystar writer
// shares. Ported from airflow-adi-service
// `lib/billing/eligibility/shadow_compare/waystar.py`, labels kept byte-equal
// to that model's keys so rows here line up with the Airflow shadow-run metrics.
//
// Deliberately not compared, matching ADI:
//
//   * payer_key — it is the job context's insurer identifier, not eligibility
//     data, so neither side can disagree about it
//   * address2 — HingeSelect's address has no second line, so comparing it would
//     read as divergence on every member who has one
//   * middle_initial / address3 — the writer hardcodes both to ""
//
// Scope caveat: this is registered for all e-file traffic, because the verifier
// runs one member and so has no resolved writer to key on the way a billing run
// does. The Waystar subclasses that read coverage data out of `extra_columns`
// (Wellfleet, HealthPartners, BCBSMI) are therefore in scope but under-reported
// — a green result does not prove those writers agree. The BCBSMA and BCBSAL
// invoice writers are the exception: their extra columns are modelled below, in
// a section of their own.

// `type`, not `interface` — see ProviderModel.
export type EfileMemberInfo = {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  sex?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  depFirstName?: string;
  depLastName?: string;
  depDateOfBirth?: string;
  depSex?: string;
  depAddress?: string;
  depCity?: string;
  depState?: string;
  depZip?: string;
  relationship?: string;
  subscriberIdentifier?: string;
  clientId?: string;
  insurerId?: string;
};

// Both sides spell names and street addresses in whatever case their source
// file used, and HingeSelect lowercases the relationship where legacy sends
// EE/Spouse/Child/Other. Comparing any of those exactly would diverge on
// healthy members. `zip` and `dateOfBirth` stay exact — neither has a case —
// and `sex` is normalized to a single letter on both sides below.
//
// `neverBlur` marks the org-level ids: a client or insurer identifies an
// organization, not a person. Everything else stays blurred.
// The writer's `correct_address_info` pass overwrites all five address columns
// from a Databricks user-address query whenever eligibility returned no street
// address, so these rows are the one part of the model that isn't the last word
// on what the claim carries. The fallback is keyed on the user, not on either
// eligibility payload — so absent-on-both is still genuine agreement (both
// paths land on the same Databricks address), but a one-sided miss may never
// reach the claim.
const ADDRESS_NOTE =
  'The writer falls back to a Databricks user address when eligibility returns none, so a divergence here may not reach the claim.';
const ZIP_NOTE = `${ADDRESS_NOTE} Only the first five digits are compared, so ZIP+4 against a plain zipcode reads as a match.`;

export const EFILE_FIELDS: readonly FieldSpec<EfileMemberInfo>[] = [
  { key: 'firstName', label: 'first_name', compare: 'caseInsensitive' },
  { key: 'lastName', label: 'last_name', compare: 'caseInsensitive' },
  { key: 'dateOfBirth', label: 'date_of_birth', compare: 'exact' },
  { key: 'sex', label: 'sex', compare: 'exact' },
  {
    key: 'address',
    label: 'address',
    compare: 'caseInsensitive',
    note: ADDRESS_NOTE,
  },
  {
    key: 'city',
    label: 'city',
    compare: 'caseInsensitive',
    note: ADDRESS_NOTE,
  },
  {
    key: 'state',
    label: 'state',
    compare: 'caseInsensitive',
    note: ADDRESS_NOTE,
  },
  { key: 'zip', label: 'zip', compare: 'exact', note: ZIP_NOTE },
  { key: 'depFirstName', label: 'dep_first_name', compare: 'caseInsensitive' },
  { key: 'depLastName', label: 'dep_last_name', compare: 'caseInsensitive' },
  { key: 'depDateOfBirth', label: 'dep_date_of_birth', compare: 'exact' },
  { key: 'depSex', label: 'dep_sex', compare: 'exact' },
  // Once the fallback fires it writes the *subscriber's* address into the dep_*
  // columns too, so a dep_address divergence can be moot in practice.
  {
    key: 'depAddress',
    label: 'dep_address',
    compare: 'caseInsensitive',
    note: ADDRESS_NOTE,
  },
  {
    key: 'depCity',
    label: 'dep_city',
    compare: 'caseInsensitive',
    note: ADDRESS_NOTE,
  },
  {
    key: 'depState',
    label: 'dep_state',
    compare: 'caseInsensitive',
    note: ADDRESS_NOTE,
  },
  { key: 'depZip', label: 'dep_zip', compare: 'exact', note: ZIP_NOTE },
  { key: 'relationship', label: 'relationship', compare: 'caseInsensitive' },
  {
    key: 'subscriberIdentifier',
    label: 'subscriber_identifier',
    compare: 'exact',
  },
  { key: 'clientId', label: 'client_id', compare: 'exact', neverBlur: true },
  { key: 'insurerId', label: 'insurer_id', compare: 'exact', neverBlur: true },
];

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

// Ported verbatim from airflow-adi-service `lib/billing/helpers.wash_float`.
// Applied on both sides because it is the value the claim actually receives, so
// it is the value whose divergence matters.
const washFloat = (value?: string): string | undefined => {
  if (value === undefined || value === null) return undefined;
  let text = String(value);
  if (text.includes('.')) [text] = text.split('.');
  if (text.startsWith('AN') && text.endsWith('0000')) {
    text = text.slice(0, -4);
  }
  return present(text.trim().replace(/^0+/, ''));
};

// Only the five-digit base zip is compared; a ZIP+4 suffix is dropped on both
// sides.
const zip5 = (value?: string): string | undefined =>
  present(value)?.slice(0, 5);

// The next three replicate what billing's pydantic models do to a legacy
// response before the shadow compare in ADI ever sees it. This side of the BFF
// is raw JSON, so without them a healthy member reads red.

// `Gender` is a MultiValueEnum accepting several spellings per letter and
// folding anything unrecognized into U.
const LEGACY_SEX_LETTER: Record<string, string> = {
  M: 'M',
  m: 'M',
  MALE: 'M',
  male: 'M',
  Male: 'M',
  '1': 'M',
  F: 'F',
  f: 'F',
  FEMALE: 'F',
  female: 'F',
  Female: 'F',
  '2': 'F',
  U: 'U',
  '0': 'U',
};

// `PatientInfo`'s validator forces an absent sex to U, so a legacy person that
// exists always reports a letter.
const legacySex = (raw?: string): string => {
  const stated = present(raw) ?? '';
  // hasOwn, not a bare lookup — an index into a plain object with a wire value
  // like "constructor" returns an inherited Object.prototype member instead of
  // falling through to the default.
  return Object.hasOwn(LEGACY_SEX_LETTER, stated)
    ? LEGACY_SEX_LETTER[stated]
    : 'U';
};

// `Relationship` is a MultiValueEnum where EE spells both "EE" and "Self", and
// `_missing_value_` folds everything else into Other. Lookup is exact, so a
// lowercase "ee" on the wire really does reach billing as Other.
const LEGACY_RELATIONSHIP: Record<string, string> = {
  EE: 'EE',
  Self: 'EE',
  Spouse: 'Spouse',
  Child: 'Child',
  Other: 'Other',
};

// `PatientInfo`'s validator forces an absent relationship to EE.
const legacyRelationship = (raw?: string): string => {
  const stated = present(raw);
  if (stated === undefined) return 'EE';
  return Object.hasOwn(LEGACY_RELATIONSHIP, stated)
    ? LEGACY_RELATIONSHIP[stated]
    : 'Other';
};

// HingeSelect speaks the proto enum. Neither side has a letter for OTHER —
// legacy folds anything unrecognized into U — so a member reported as OTHER is
// a real divergence and is left to read as one.
const INTERNAL_SEX_LETTER: Record<string, string> = {
  SEX_MALE: 'M',
  SEX_FEMALE: 'F',
  SEX_OTHER: 'O',
  SEX_UNKNOWN: 'U',
  SEX_UNSPECIFIED: 'U',
};

// Absent reads as U, not as missing. SEX_UNKNOWN is the proto's zero value and
// protojson omits zero values, so an unreported sex arrives as no field at all
// — and billing's own model defaults it straight back to SEX_UNKNOWN. Returning
// undefined here would put "Missing on internal" against legacy's U on every
// member whose sex nobody reported.
const internalSex = (raw?: string): string => {
  const stated = present(raw) ?? '';
  return Object.hasOwn(INTERNAL_SEX_LETTER, stated)
    ? INTERNAL_SEX_LETTER[stated]
    : 'U';
};

// ---------------------------------------------------------------------------
// The two halves of the model
// ---------------------------------------------------------------------------

// A person as the writer needs them, or undefined where the coverage reports
// nobody.
type Person =
  | {
      firstName?: string;
      lastName?: string;
      dateOfBirth?: string;
      sex?: string;
      address?: string;
      city?: string;
      state?: string;
      zip?: string;
    }
  | undefined;

// Drop a person neither path could bill as the subscriber.
//
// `WaystarWriterMemberInfoBase` requires first and last name while every dep_*
// field is optional, so a name-less block is nobody — that is the writer's own
// requirement, not a heuristic.
//
// This is also how HingeSelect says it has no separate subscriber: a fully
// blank `subscriber` block, where legacy simply omits `primarySubscriberInfo`.
// One predicate for both sides, because a side that reads a blank block as a
// real person inverts the two halves of the model — the subscriber fields read
// missing while every dep_* field reads missing on the *other* side, and a
// member whose data agrees perfectly reports sixteen divergences naming nothing
// that is actually wrong.
const reported = (person: NonNullable<Person>): Person =>
  person.firstName === undefined && person.lastName === undefined
    ? undefined
    : person;

// The absence check is falsy rather than `=== undefined`: the legacy side is
// pydantic-serialized, which renders an unset `primarySubscriberInfo` as an
// explicit null rather than omitting the key.
const fromLegacyPerson = (info?: LegacyPatientInfo | null): Person =>
  !info
    ? undefined
    : reported({
        firstName: present(info.firstName),
        lastName: present(info.lastName),
        dateOfBirth: present(info.dateOfBirth),
        sex: legacySex(info.sex),
        address: present(info.address),
        city: present(info.city),
        state: present(info.state),
        zip: zip5(info.postalCode),
      });

const fromInternalPerson = (
  person?: InternalEligibilityPerson | null,
): Person =>
  !person
    ? undefined
    : reported({
        // firstNameLegal, not preferred — the writer sends the legal name.
        firstName: present(person.firstNameLegal),
        lastName: present(person.lastName),
        dateOfBirth: present(person.dateOfBirth),
        sex: internalSex(person.sex),
        address: present(person.address?.address1),
        city: present(person.address?.city),
        state: present(person.address?.state),
        zip: zip5(person.address?.zipcode),
      });

const subscriberHalf = (person: Person): Partial<EfileMemberInfo> => ({
  firstName: person?.firstName,
  lastName: person?.lastName,
  dateOfBirth: person?.dateOfBirth,
  sex: person?.sex,
  address: person?.address,
  city: person?.city,
  state: person?.state,
  zip: person?.zip,
});

const dependentHalf = (person: Person): Partial<EfileMemberInfo> => ({
  depFirstName: person?.firstName,
  depLastName: person?.lastName,
  depDateOfBirth: person?.dateOfBirth,
  depSex: person?.sex,
  depAddress: person?.address,
  depCity: person?.city,
  depState: person?.state,
  depZip: person?.zip,
});

// Relationships meaning the patient is the subscriber, mirroring legacy
// exactly. Anything else — including "unknown", which HingeSelect does emit —
// reads as a dependent. Legacy's rules decide this rather than a judgment of
// our own: the point of the comparison is to reproduce what the old path does.
const SUBSCRIBER_RELATIONSHIPS = ['', 'ee', 'self'];

// Assign the two people the writer's three coverage scenarios produce.
//
// Mirrors `WaystarWriter.get_patient_details`: with a separate subscriber the
// patient is the dependent; without one an EE patient *is* the subscriber, and
// a non-EE patient leaves the subscriber unreported — the case the writer drops
// as a missing primary subscriber.
//
// An unstated relationship reads as EE, which is what both legacy's validator
// and the writer's own fallback branch do. Defaulting it the other way empties
// the subscriber half against a legacy side that filled it, turning one
// unstated field into nine divergent rows.
//
// Relationship decides the branch, not merely whether a `subscriber` block
// came back: HingeSelect echoes a populated (patient-equal) subscriber block
// even for EE, where legacy omits it outright. Branching on block presence
// alone reads that redundant echo as a real dependent and reports nine
// phantom divergences on a healthy EE member.
//
// `unclaimedPatientIsDependent` is an intentional divergence from the ported
// writer, scoped to HingeSelect only: for a non-EE patient with no separate
// subscriber, `fromInternal` still knows the patient (unlike the writer, which
// has already given up on a missing primary subscriber by that point), so this
// tool reports it as the dependent instead of dropping it. `fromLegacy` keeps
// mirroring the writer's drop exactly, since that side's whole job is to model
// what the writer really does.
const split = (
  patient: Person,
  subscriber: Person,
  relationship: string | undefined,
  unclaimedPatientIsDependent: boolean,
): Partial<EfileMemberInfo> => {
  const stated = (relationship ?? '').trim().toLowerCase();
  const isSubscriber = SUBSCRIBER_RELATIONSHIPS.includes(stated);

  if (!isSubscriber && subscriber !== undefined) {
    return { ...subscriberHalf(subscriber), ...dependentHalf(patient) };
  }

  return {
    ...subscriberHalf(isSubscriber ? patient : undefined),
    ...dependentHalf(
      !isSubscriber && unclaimedPatientIsDependent ? patient : undefined,
    ),
  };
};

// ---------------------------------------------------------------------------
// Extractors
// ---------------------------------------------------------------------------

export const fromLegacy = (
  payload: LegacyEligibilityStatus,
): EfileMemberInfo => {
  const coverage = payload.coverageInfo;
  const patient = coverage?.patientInfo;
  // The EE default belongs to a patient that exists — it is what the validator
  // does to an unstated field on a real person. With no patientInfo at all
  // there is no coverage to have a relationship, and fabricating EE here puts a
  // phantom row against an internal side that correctly reports nothing.
  const relationship = patient
    ? legacyRelationship(patient.relationship)
    : undefined;

  return {
    ...split(
      fromLegacyPerson(patient),
      fromLegacyPerson(coverage?.primarySubscriberInfo),
      relationship,
      false,
    ),
    relationship,
    // Read off the patient, not the subscriber — that is where the writer takes
    // it from.
    subscriberIdentifier: washFloat(patient?.subscriberId),
    clientId: present(payload.clientId),
    insurerId: present(payload.insurerId),
  };
};

export const fromInternal = (
  payload: InternalEligibilityResponse,
): EfileMemberInfo => ({
  ...split(
    fromInternalPerson(payload.patient),
    fromInternalPerson(payload.subscriber),
    payload.relationship,
    true,
  ),
  relationship: present(payload.relationship),
  subscriberIdentifier: washFloat(payload.patient?.subscriberId),
  clientId: present(payload.clientId),
  // insurerId is only assigned once verification succeeds; every earlier exit
  // leaves it 0, which is absence rather than a value.
  insurerId: present(payload.insurerId || undefined),
});

// ---------------------------------------------------------------------------
// Efile extra columns (BCBSMA / BCBSAL invoice writers)
// ---------------------------------------------------------------------------

// Ported from airflow-adi-service `lib/billing/writers/invoice/bcbsma.py` and
// `bcbsal.py`. Each writer reads its own keys out of `efileRecord.extra_columns`
// — `grp_num` for BCBSMA where BCBSAL reads `grp_nr` — so every writer gets its
// own row even where the field name is shared. Nothing here is merged.
//
// Internal keys are the BFF's camelCasing of the same names, read off
// `verificationState.rawResponse`. A key listed second is a fallback, tried in
// order exactly as the writer does. `padTo` mirrors the writer's zfill, applied
// on both sides so a dropped leading zero doesn't read as a mismatch.
interface ExtraColumnSpec {
  writer: 'BCBSMA' | 'BCBSAL';
  field: string;
  legacyKeys: readonly string[];
  internalKeys: readonly string[];
  padTo?: number;
}

export const EXTRA_COLUMN_SPECS: readonly ExtraColumnSpec[] = [
  {
    writer: 'BCBSMA',
    field: 'unique_person_id',
    legacyKeys: ['unique_person_id'],
    internalKeys: ['unqMemId', 'externalIdentifier'],
  },
  {
    writer: 'BCBSMA',
    field: 'account_number',
    legacyKeys: ['acc_num'],
    internalKeys: ['accNum'],
    // padTo: 7,
  },
  {
    writer: 'BCBSMA',
    field: 'account_name',
    legacyKeys: ['acc_name'],
    internalKeys: ['accName'],
  },
  {
    writer: 'BCBSMA',
    field: 'group_number',
    legacyKeys: ['grp_num'],
    internalKeys: ['grpNum'],
    // padTo: 9,
  },
  {
    writer: 'BCBSMA',
    field: 'group_name',
    legacyKeys: ['grp_name'],
    internalKeys: ['grpName'],
  },
  {
    writer: 'BCBSAL',
    field: 'unique_person_id',
    legacyKeys: ['unq_mem_id', 'external_identifier'],
    internalKeys: ['unqMemId', 'externalIdentifier'],
    // padTo: 9,
  },
  {
    writer: 'BCBSAL',
    field: 'corp_cd',
    legacyKeys: ['corp_cd'],
    internalKeys: ['corpCd'],
    // padTo: 9,
  },
  {
    writer: 'BCBSAL',
    field: 'group_number',
    legacyKeys: ['grp_nr'],
    internalKeys: ['grpNr'],
    // padTo: 9,
  },
  {
    writer: 'BCBSAL',
    field: 'div',
    legacyKeys: ['div'],
    internalKeys: ['div'],
  },
];

const extraKey = (spec: ExtraColumnSpec): string =>
  `${spec.writer.toLowerCase()}.${spec.field}`;

const readExtra = (
  record: Record<string, unknown> | undefined,
  keys: readonly string[],
  padTo?: number,
): string | undefined => {
  const hit = keys
    .map(key => present(record?.[key]))
    .find(v => v !== undefined);

  return padTo === undefined ? hit : hit?.padStart(padTo, '0');
};

const EXTRA_FIELDS: readonly FieldSpec<ProviderModel>[] =
  EXTRA_COLUMN_SPECS.map(spec => ({
    key: extraKey(spec),
    label: spec.field,
    compare: 'exact',
    appliesTo: spec.writer,
  }));

export const extraColumnRows = (
  legacy: LegacyEligibilityStatus,
  internal: InternalEligibilityResponse,
): DiffRow[] => {
  const legacyRecord = legacy.coverageInfo?.coverageDetails?.efileRecord
    ?.extra_columns as Record<string, unknown> | undefined;
  const internalRecord = internal.states?.verificationState?.rawResponse;
  // Internal group numbers come off the subscriber, like subscriberIdentifier
  // comes off the patient, rather than out of the raw verification response.
  const internalGroupNumber = present(internal.subscriber?.groupNumber);
  const model = (
    keysOf: (spec: ExtraColumnSpec) => readonly string[],
    record?: Record<string, unknown>,
    groupNumber?: string,
  ): ProviderModel =>
    Object.fromEntries(
      EXTRA_COLUMN_SPECS.map(spec => [
        extraKey(spec),
        spec.field === 'group_number' && groupNumber !== undefined
          ? groupNumber
          : readExtra(record, keysOf(spec), spec.padTo),
      ]),
    );

  return buildRows(
    EXTRA_FIELDS.map((field, index) => ({
      ...field,
      note: `Reads ${EXTRA_COLUMN_SPECS[index].legacyKeys.join(' → ')} (legacy) / ${EXTRA_COLUMN_SPECS[index].internalKeys.join(' → ')} (internal)`,
    })),
    model(spec => spec.legacyKeys, legacyRecord),
    model(spec => spec.internalKeys, internalRecord, internalGroupNumber),
  );
};

export const efileComparator: ProviderComparator = {
  provider: 'E-file',
  legacyApiSources: ['FILE'],
  internalProviders: ['ELIGIBILITY_PROVIDER_EFILE'],
  compare: (legacy, internal) =>
    buildRows(EFILE_FIELDS, fromLegacy(legacy), fromInternal(internal)),
  // The e-file path has no counterpart to ESI's three unrelated notions of
  // "status" — there is no upstream call whose outcome could need context.
  context: () => [],
  extraColumns: extraColumnRows,
  project: (legacy, internal) => ({
    legacy: fromLegacy(legacy),
    internal: fromInternal(internal),
  }),
};
